import {
  AnimationDirection,
  FilterType,
  FilterUnit,
  IFilterItem,
  ILoopingAnimationItem,
  ILoopingKeyframeStop,
  ITransformItem,
  TransformType,
  TransformUnit,
  TransitionTimingFunction
} from "@kuika/kuika-cl-model";
import { v4 as uuidv4 } from "uuid";
import type { KuikaSelectboxOption } from "../../../ui-kit/kuika-selectbox/kuika-selectbox";

/**
 * Looping (repeated) animation helpers. The Effects panel exposes a list of
 * `animations` per component. Each item is either a preset (which pre-fills a
 * set of keyframe stops) or "custom" (hand-edited stops) — the model stores the
 * resolved stops either way, so the React generator emits multi-stop
 * `@keyframes` uniformly. Web-only — native generators never read it. Shared
 * between the panel UI and Vitest pure tests.
 */
export type LoopingPresetKey =
  | "pulse"
  | "float"
  | "spin"
  | "bounce"
  | "shake"
  | "glow"
  | "ping"
  | "shimmer"
  | "heartbeat"
  | "custom";

// id-less inputs for the static preset library; fresh UUIDs are minted per
// application in hydrateStops so two components never share an item id.
interface LoopingPresetTransformInput {
  type: TransformType;
  x?: number;
  y?: number;
  z?: number;
  unit?: TransformUnit;
}

interface LoopingPresetFilterInput {
  type: FilterType;
  value?: number;
  unit?: FilterUnit;
  offsetX?: number;
  offsetY?: number;
  blur?: number;
  color?: string;
}

interface LoopingPresetStop {
  offset: number;
  transforms?: LoopingPresetTransformInput[];
  filters?: LoopingPresetFilterInput[];
  opacity?: number;
}

export interface LoopingPreset {
  key: LoopingPresetKey;
  label: string;
  durationMs: number;
  timingFunction: TransitionTimingFunction;
  direction: AnimationDirection;
  stops: LoopingPresetStop[];
}

export const LOOPING_ANIMATION_DEFAULT_DURATION: number = 1000;

/**
 * 9 looping presets covering ~90% of the "living UI" motion in the reference
 * demos. Each is expressible on a single element with transform / filter /
 * opacity (no pseudo-elements, no gradients), so it round-trips cleanly through
 * the keyframe-stop model. Equalizer-bars / shine-sweep are intentionally
 * omitted — they need per-child stagger or a pseudo-element layer (separate
 * roadmap items).
 */
export const LOOPING_PRESETS: LoopingPreset[] = [
  {
    key: "pulse",
    label: "Pulse",
    durationMs: 1600,
    timingFunction: "ease-in-out",
    direction: "normal",
    stops: [
      { offset: 0, transforms: [{ type: "scale", x: 1, y: 1 }] },
      { offset: 50, transforms: [{ type: "scale", x: 1.08, y: 1.08 }] },
      { offset: 100, transforms: [{ type: "scale", x: 1, y: 1 }] }
    ]
  },
  {
    key: "float",
    label: "Float",
    durationMs: 3000,
    timingFunction: "ease-in-out",
    direction: "alternate",
    stops: [
      { offset: 0, transforms: [{ type: "move", x: 0, y: 0 }] },
      { offset: 100, transforms: [{ type: "move", x: 0, y: -12 }] }
    ]
  },
  {
    key: "spin",
    label: "Spin",
    durationMs: 4000,
    timingFunction: "linear",
    direction: "normal",
    stops: [
      { offset: 0, transforms: [{ type: "rotate", z: 0 }] },
      { offset: 100, transforms: [{ type: "rotate", z: 360 }] }
    ]
  },
  {
    key: "bounce",
    label: "Bounce",
    durationMs: 1400,
    timingFunction: "ease",
    direction: "normal",
    stops: [
      { offset: 0, transforms: [{ type: "move", x: 0, y: 0 }] },
      { offset: 25, transforms: [{ type: "move", x: 0, y: -18 }] },
      { offset: 50, transforms: [{ type: "move", x: 0, y: 0 }] },
      { offset: 75, transforms: [{ type: "move", x: 0, y: -9 }] },
      { offset: 100, transforms: [{ type: "move", x: 0, y: 0 }] }
    ]
  },
  {
    key: "shake",
    label: "Shake",
    durationMs: 600,
    timingFunction: "ease-in-out",
    direction: "normal",
    stops: [
      { offset: 0, transforms: [{ type: "move", x: 0, y: 0 }] },
      { offset: 20, transforms: [{ type: "move", x: -8, y: 0 }] },
      { offset: 40, transforms: [{ type: "move", x: 8, y: 0 }] },
      { offset: 60, transforms: [{ type: "move", x: -8, y: 0 }] },
      { offset: 80, transforms: [{ type: "move", x: 8, y: 0 }] },
      { offset: 100, transforms: [{ type: "move", x: 0, y: 0 }] }
    ]
  },
  {
    key: "glow",
    label: "Glow Pulse",
    durationMs: 1800,
    timingFunction: "ease-in-out",
    direction: "normal",
    stops: [
      { offset: 0, filters: [{ type: "drop-shadow", offsetX: 0, offsetY: 0, blur: 2, color: "rgba(99,102,241,0.5)" }] },
      {
        offset: 50,
        filters: [{ type: "drop-shadow", offsetX: 0, offsetY: 0, blur: 16, color: "rgba(99,102,241,0.9)" }]
      },
      {
        offset: 100,
        filters: [{ type: "drop-shadow", offsetX: 0, offsetY: 0, blur: 2, color: "rgba(99,102,241,0.5)" }]
      }
    ]
  },
  {
    key: "ping",
    label: "Ping / Radar",
    durationMs: 1600,
    timingFunction: "ease-out",
    direction: "normal",
    stops: [
      { offset: 0, transforms: [{ type: "scale", x: 1, y: 1 }], opacity: 1 },
      { offset: 100, transforms: [{ type: "scale", x: 2.2, y: 2.2 }], opacity: 0 }
    ]
  },
  {
    key: "shimmer",
    label: "Shimmer",
    durationMs: 2000,
    timingFunction: "ease-in-out",
    direction: "normal",
    stops: [
      { offset: 0, filters: [{ type: "brightness", value: 1, unit: "none" }] },
      { offset: 50, filters: [{ type: "brightness", value: 1.6, unit: "none" }] },
      { offset: 100, filters: [{ type: "brightness", value: 1, unit: "none" }] }
    ]
  },
  {
    key: "heartbeat",
    label: "Heartbeat",
    durationMs: 1400,
    timingFunction: "ease-in-out",
    direction: "normal",
    stops: [
      { offset: 0, transforms: [{ type: "scale", x: 1, y: 1 }] },
      { offset: 15, transforms: [{ type: "scale", x: 1.15, y: 1.15 }] },
      { offset: 30, transforms: [{ type: "scale", x: 1, y: 1 }] },
      { offset: 45, transforms: [{ type: "scale", x: 1.15, y: 1.15 }] },
      { offset: 60, transforms: [{ type: "scale", x: 1, y: 1 }] },
      { offset: 100, transforms: [{ type: "scale", x: 1, y: 1 }] }
    ]
  }
];

const LOOPING_PRESET_KEYS: Set<string> = new Set<string>([
  ...LOOPING_PRESETS.map((p: LoopingPreset): string => p.key),
  "custom"
]);

/** Preset dropdown options (9 presets + Custom). */
export const LOOPING_PRESET_OPTIONS: KuikaSelectboxOption[] = [
  ...LOOPING_PRESETS.map((p: LoopingPreset): KuikaSelectboxOption => ({ value: p.key, label: p.label })),
  { value: "custom", label: "Custom" }
];

export const ANIMATION_DIRECTION_OPTIONS: KuikaSelectboxOption[] = [
  { value: "normal", label: "Normal" },
  { value: "reverse", label: "Reverse" },
  { value: "alternate", label: "Alternate" },
  { value: "alternate-reverse", label: "Alternate Reverse" }
];

const hydrateStops = (stops: LoopingPresetStop[]): ILoopingKeyframeStop[] =>
  stops.map((s: LoopingPresetStop): ILoopingKeyframeStop => {
    const stop: ILoopingKeyframeStop = { offset: s.offset };
    if (s.transforms) {
      stop.transforms = s.transforms.map((t: LoopingPresetTransformInput): ITransformItem => ({ id: uuidv4(), ...t }));
    }
    if (s.filters) {
      stop.filters = s.filters.map((f: LoopingPresetFilterInput): IFilterItem => ({ id: uuidv4(), ...f }));
    }
    if (s.opacity !== undefined) stop.opacity = s.opacity;
    return stop;
  });

/**
 * Build a fresh looping animation item for a chosen preset (or a "custom"
 * starter with a 2-stop opacity timeline the user then edits). Transform/filter
 * items get fresh UUIDs so the same preset applied to two components never
 * shares an item id.
 */
export const defaultLoopingAnimationItem = (presetKey: LoopingPresetKey): ILoopingAnimationItem => {
  const preset: LoopingPreset | undefined = LOOPING_PRESETS.find((p: LoopingPreset): boolean => p.key === presetKey);
  if (!preset) {
    return {
      id: uuidv4(),
      presetKey: "custom",
      durationMs: LOOPING_ANIMATION_DEFAULT_DURATION,
      timingFunction: "linear",
      infinite: true,
      direction: "normal",
      keyframes: [
        { offset: 0, opacity: 1 },
        { offset: 100, opacity: 1 }
      ]
    };
  }
  return {
    id: uuidv4(),
    presetKey: preset.key,
    durationMs: preset.durationMs,
    timingFunction: preset.timingFunction,
    infinite: true,
    direction: preset.direction,
    keyframes: hydrateStops(preset.stops)
  };
};

/** A fresh empty keyframe stop for the custom keyframe editor's "add stop". */
export const defaultKeyframeStop = (offset: number): ILoopingKeyframeStop => ({ offset });

/**
 * Which preset (if any) a stored item came from, for the dropdown. Once the
 * user hand-edits the stops the UI sets presetKey to "custom", so this just
 * reads it back (falling through to "custom" for unknown/missing keys).
 */
export const detectLoopingPreset = (item: ILoopingAnimationItem): LoopingPresetKey => {
  const key: string | undefined = item.presetKey;
  if (key && LOOPING_PRESET_KEYS.has(key)) return key as LoopingPresetKey;
  return "custom";
};

/** Read-only summary shown in the looping-animation PopoverInput row. */
export const summarizeLoopingAnimation = (item: ILoopingAnimationItem): string => {
  const preset: LoopingPreset | undefined = LOOPING_PRESETS.find(
    (p: LoopingPreset): boolean => p.key === item.presetKey
  );
  const label: string = preset ? preset.label : "Custom";
  const parts: string[] = [label, `${item.durationMs ?? LOOPING_ANIMATION_DEFAULT_DURATION}ms`];
  parts.push(item.infinite === false ? `${item.iterationCount ?? 1}×` : "∞");
  if (item.direction && item.direction !== "normal") {
    parts.push(item.direction);
  }
  return parts.join(" · ");
};

/** Clamp + sort keyframe stops by offset (0–100). Used by the stop editor on commit. */
export const normalizeKeyframeStops = (stops: ILoopingKeyframeStop[]): ILoopingKeyframeStop[] =>
  [...stops]
    .map((s: ILoopingKeyframeStop): ILoopingKeyframeStop => ({ ...s, offset: Math.min(100, Math.max(0, s.offset)) }))
    .sort((a: ILoopingKeyframeStop, b: ILoopingKeyframeStop): number => a.offset - b.offset);
